import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, CreditCard, Euro, Clock, CheckCircle, AlertTriangle,
  Search, Receipt, Calendar, ChevronRight, ArrowRight,
  FileText, PlusCircle, User, MapPin
} from 'lucide-react';
import {
  getFilteredAffaires, getFilteredFactures,
  getClientById, getIngenieurById, getSocieteById,
  statutFactureLabels, typeFactureLabels, getFilteredClients
} from '../data/mockData';
import { useFilters } from '../App';

const Facturation = () => {
  const filters = useFilters();
  const [activeTab, setActiveTab] = useState('previsionnel');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterClient, setFilterClient] = useState('');
  const [filterAdresse, setFilterAdresse] = useState('');
  const [search, setSearch] = useState('');

  const allAffaires = getFilteredAffaires(filters);
  const allFactures = getFilteredFactures(filters);
  const allClients = getFilteredClients(filters);

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(montant);
  };

  // Mois pour le tableau prévisionnel (6 mois)
  const months = [];
  const today = new Date();
  for (let i = -2; i < 6; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }),
      full: d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
    });
  }

  const affairesEnCours = allAffaires.filter(a => a.statut === 'en_cours');

  // Statistiques facturation
  const totalFacture = allFactures.filter(f => f.statut === 'payee').reduce((acc, f) => acc + f.montantHT, 0);
  const totalEnCours = allFactures.filter(f => f.statut === 'envoyee').reduce((acc, f) => acc + f.montantHT, 0);
  const totalEnRetard = allFactures.filter(f => f.statut === 'en_retard').reduce((acc, f) => acc + f.montantHT, 0);
  const totalAFacturer = allAffaires
    .filter(a => a.statut === 'en_cours')
    .reduce((acc, a) => {
      const dejaFacture = allFactures
        .filter(f => f.affaireId === a.id && f.statut !== 'brouillon')
        .reduce((s, f) => s + f.montantHT, 0);
      return acc + Math.max(0, a.montantHT - dejaFacture);
    }, 0);

  // Options pour les filtres
  const clientOptions = allClients
    .filter(c => allFactures.some(f => f.clientId === c.id))
    .map(c => ({ value: c.id, label: c.nom }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const adresseOptions = [...new Set(
    allFactures
      .map(f => {
        const affaire = allAffaires.find(a => a.id === f.affaireId);
        return affaire?.ville;
      })
      .filter(Boolean)
  )]
    .sort()
    .map(v => ({ value: v, label: v }));

  // Filtrer factures
  const filteredFactures = allFactures.filter(f => {
    const client = getClientById(f.clientId);
    const affaire = allAffaires.find(a => a.id === f.affaireId);
    const matchSearch = f.numero.toLowerCase().includes(search.toLowerCase()) ||
      client?.nom.toLowerCase().includes(search.toLowerCase());
    const matchStatut = filterStatut === 'tous' || f.statut === filterStatut;
    const matchClient = !filterClient || f.clientId === parseInt(filterClient);
    const matchAdresse = !filterAdresse || affaire?.ville === filterAdresse;
    return matchSearch && matchStatut && matchClient && matchAdresse;
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><TrendingUp size={28} /> Facturation</h1>
          <div className="page-meta">
            <span>{allFactures.length} factures</span>
            <span>{allFactures.filter(f => f.statut === 'en_retard').length} en retard</span>
          </div>
        </div>
        <div className="page-actions">
          <Link to="/factures/nouvelle" className="btn btn-primary">
            <PlusCircle size={18} /> Créer une facture
          </Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="stat-card stat-success">
          <div className="stat-icon"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Payé</p>
            <p className="stat-value">{formatMontant(totalFacture)}</p>
          </div>
        </div>
        <div className="stat-card stat-primary">
          <div className="stat-icon"><Clock size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">En attente paiement</p>
            <p className="stat-value">{formatMontant(totalEnCours)}</p>
          </div>
        </div>
        <div className="stat-card stat-warning">
          <div className="stat-icon"><Euro size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">À facturer</p>
            <p className="stat-value">{formatMontant(totalAFacturer)}</p>
          </div>
        </div>
        <div className="stat-card" style={{ border: totalEnRetard > 0 ? '2px solid var(--danger)' : undefined }}>
          <div className="stat-icon" style={{ background: '#FEE2E2', color: '#DC2626' }}><AlertTriangle size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">En retard</p>
            <p className="stat-value" style={{ color: totalEnRetard > 0 ? 'var(--danger)' : undefined }}>{formatMontant(totalEnRetard)}</p>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <button
          className={`filter-tab ${activeTab === 'previsionnel' ? 'active' : ''}`}
          onClick={() => setActiveTab('previsionnel')}
        >
          <Calendar size={16} /> Tableau prévisionnel
        </button>
        <button
          className={`filter-tab ${activeTab === 'factures' ? 'active' : ''}`}
          onClick={() => setActiveTab('factures')}
        >
          <Receipt size={16} /> Liste des factures
        </button>
        <button
          className={`filter-tab ${activeTab === 'retard' ? 'active' : ''}`}
          onClick={() => setActiveTab('retard')}
          style={allFactures.filter(f => f.statut === 'en_retard').length > 0 ? { borderColor: 'var(--danger)', color: 'var(--danger)' } : undefined}
        >
          <AlertTriangle size={16} /> En retard ({allFactures.filter(f => f.statut === 'en_retard').length})
        </button>
      </div>

      {/* Tableau prévisionnel */}
      {activeTab === 'previsionnel' && (
        <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-200)', overflow: 'hidden' }}>
          <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
              Tableau prévisionnel de facturation — Affaires en cours
            </h3>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
              <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '2px', background: '#F97316', marginRight: '0.25rem', verticalAlign: 'middle' }}></span> Facturé
              <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '2px', background: '#DC2626', marginRight: '0.25rem', marginLeft: '1rem', verticalAlign: 'middle' }}></span> En retard
              <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '2px', background: '#059669', marginRight: '0.25rem', marginLeft: '1rem', verticalAlign: 'middle' }}></span> Payé
            </p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1100px' }}>
              <thead>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', background: 'white', borderBottom: '2px solid var(--gray-200)', position: 'sticky', left: 0, zIndex: 1 }}>N° Affaire</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', background: 'white', borderBottom: '2px solid var(--gray-200)' }}>Client</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', background: 'white', borderBottom: '2px solid var(--gray-200)' }}>Soc.</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', background: 'white', borderBottom: '2px solid var(--gray-200)' }}>Description</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', background: 'white', borderBottom: '2px solid var(--gray-200)' }}>Honoraires</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', background: 'white', borderBottom: '2px solid var(--gray-200)' }}>Ing.</th>
                  {months.map(m => (
                    <th key={m.key} style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--gray-600)', textTransform: 'uppercase', background: 'white', borderBottom: '2px solid var(--gray-200)', minWidth: '80px' }}>
                      {m.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {affairesEnCours.map(a => {
                  const client = getClientById(a.clientId);
                  const ing = getIngenieurById(a.ingenieurId);
                  const societe = getSocieteById(a.societeId);
                  return (
                    <tr key={a.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '0.625rem 1rem', position: 'sticky', left: 0, background: 'white', zIndex: 1 }}>
                        <Link to={`/affaires/${a.id}`} style={{ textDecoration: 'none' }}>
                          <span className="facture-numero-cell">{a.numero}</span>
                        </Link>
                      </td>
                      <td style={{ padding: '0.625rem 1rem' }}>
                        <Link to={`/clients/${a.clientId}`} style={{ textDecoration: 'none', color: 'var(--gray-900)', fontSize: '0.8125rem', fontWeight: 500 }}>
                          {client?.nom}
                        </Link>
                      </td>
                      <td style={{ padding: '0.625rem 0.5rem' }}>
                        <span className="badge" style={{
                          background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                          color: societe?.id === 1 ? '#1E40AF' : '#9A3412',
                          fontSize: '0.5625rem'
                        }}>
                          {societe?.nom?.split(' ')[0]}
                        </span>
                      </td>
                      <td style={{ padding: '0.625rem 1rem', fontSize: '0.8125rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.designation}</td>
                      <td style={{ padding: '0.625rem 1rem', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>{formatMontant(a.montantHT)}</td>
                      <td style={{ padding: '0.625rem 1rem' }}>
                        <div className="user-avatar user-avatar-xs" style={{ background: ing?.couleur }} title={`${ing?.prenom} ${ing?.nom}`}>
                          {ing?.initiales}
                        </div>
                      </td>
                      {months.map(m => {
                        const echeance = a.echeances.find(e => e.moisPrevu === m.key);
                        if (!echeance) return <td key={m.key} style={{ padding: '0.625rem 0.5rem', textAlign: 'center' }}>-</td>;

                        let bgColor = '#FEF3C7'; // À facturer
                        let textColor = '#D97706';
                        if (echeance.statut === 'paye') { bgColor = '#D1FAE5'; textColor = '#059669'; }
                        else if (echeance.statut === 'facture') { bgColor = '#FFEDD5'; textColor = '#EA580C'; }
                        else if (echeance.statut === 'en_retard') { bgColor = '#FEE2E2'; textColor = '#DC2626'; }

                        return (
                          <td key={m.key} style={{ padding: '0.375rem', textAlign: 'center' }}>
                            <div style={{
                              padding: '0.375rem 0.25rem', borderRadius: '6px', background: bgColor,
                              color: textColor, fontWeight: 600, fontSize: '0.75rem'
                            }}>
                              {formatMontant(echeance.montant)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Liste des factures */}
      {activeTab === 'factures' && (
        <div>
          <div className="filters-bar">
            <div className="search-input-group">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Rechercher par numéro ou client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select className="filter-select" value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)}>
              <option value="tous">Tous les statuts</option>
              <option value="brouillon">Brouillon</option>
              <option value="envoyee">Envoyée</option>
              <option value="payee">Payée</option>
              <option value="en_retard">En retard</option>
            </select>
            <select
              className="filter-select"
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              style={{
                border: filterClient ? '2px solid var(--primary)' : undefined,
                background: filterClient ? 'var(--primary-50)' : undefined,
                color: filterClient ? 'var(--primary)' : undefined,
              }}
            >
              <option value="">Tous les clients</option>
              {clientOptions.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filterAdresse}
              onChange={(e) => setFilterAdresse(e.target.value)}
              style={{
                border: filterAdresse ? '2px solid var(--primary)' : undefined,
                background: filterAdresse ? 'var(--primary-50)' : undefined,
                color: filterAdresse ? 'var(--primary)' : undefined,
              }}
            >
              <option value="">Toutes les adresses</option>
              {adresseOptions.map(a => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>
          <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-200)', overflow: 'hidden' }}>
            <table className="factures-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>N° Facture</th>
                  <th>Client</th>
                  <th>Société</th>
                  <th>Affaire</th>
                  <th>Type</th>
                  <th>Date envoi</th>
                  <th>Échéance</th>
                  <th>Montant HT</th>
                  <th>TTC</th>
                  <th>Statut</th>
                  <th>Paiement</th>
                </tr>
              </thead>
              <tbody>
                {filteredFactures.map(f => {
                  const client = getClientById(f.clientId);
                  const affaire = allAffaires.find(a => a.id === f.affaireId) || { numero: '-' };
                  const societe = getSocieteById(f.societeId);
                  const fStatut = statutFactureLabels[f.statut];
                  const fType = typeFactureLabels[f.type];
                  return (
                    <tr key={f.id} style={f.statut === 'en_retard' ? { background: '#FEF2F2' } : undefined}>
                      <td><span className="facture-numero-cell">{f.numero}</span></td>
                      <td style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{client?.nom}</td>
                      <td>
                        <span className="badge" style={{
                          background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                          color: societe?.id === 1 ? '#1E40AF' : '#9A3412',
                          fontSize: '0.625rem'
                        }}>
                          {societe?.nom}
                        </span>
                      </td>
                      <td>
                        <Link to={`/affaires/${f.affaireId}`} style={{ fontSize: '0.8125rem', color: 'var(--primary)', textDecoration: 'none' }}>
                          {affaire?.numero}
                        </Link>
                      </td>
                      <td><span className="statut-badge" style={{ background: fType.bg, color: fType.color }}>{fType.label}</span></td>
                      <td style={{ fontSize: '0.8125rem' }}>{f.dateEnvoi ? new Date(f.dateEnvoi).toLocaleDateString('fr-FR') : '-'}</td>
                      <td style={{ fontSize: '0.8125rem' }}>{f.dateEcheance ? new Date(f.dateEcheance).toLocaleDateString('fr-FR') : '-'}</td>
                      <td style={{ fontWeight: 600 }}>{formatMontant(f.montantHT)}</td>
                      <td style={{ fontSize: '0.8125rem' }}>{formatMontant(f.montantTTC)}</td>
                      <td><span className="statut-badge" style={{ background: fStatut.bg, color: fStatut.color }}>{fStatut.label}</span></td>
                      <td style={{ fontSize: '0.8125rem' }}>{f.datePaiement ? new Date(f.datePaiement).toLocaleDateString('fr-FR') : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* En retard */}
      {activeTab === 'retard' && (
        <div>
          {allFactures.filter(f => f.statut === 'en_retard').length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {allFactures.filter(f => f.statut === 'en_retard').map(f => {
                const client = getClientById(f.clientId);
                const affaire = allAffaires.find(a => a.id === f.affaireId);
                const societe = getSocieteById(f.societeId);
                return (
                  <div key={f.id} className="paiement-card paiement-attente">
                    <div className="paiement-card-header">
                      <div className="paiement-famille">
                        <div className="famille-avatar" style={{ width: '40px', height: '40px', fontSize: '1rem', background: 'linear-gradient(135deg, #DC2626, #B91C1C)' }}>
                          {client?.nom.charAt(0)}
                        </div>
                        <div>
                          <h3>{client?.nom}</h3>
                          <span className="facture-ref">{f.numero} — {affaire?.designation}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className="badge" style={{
                          background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                          color: societe?.id === 1 ? '#1E40AF' : '#9A3412',
                        }}>
                          {societe?.nom}
                        </span>
                        <div className="retard-badge">
                          <AlertTriangle size={14} />
                          En retard depuis le {new Date(f.dateEcheance).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </div>
                    <div className="paiement-card-body">
                      <div className="paiement-info-grid">
                        <div className="paiement-info">
                          <span className="label">Montant HT</span>
                          <span className="value" style={{ fontSize: '1.25rem', color: 'var(--danger)' }}>{formatMontant(f.montantHT)}</span>
                        </div>
                        <div className="paiement-info">
                          <span className="label">Montant TTC</span>
                          <span className="value">{formatMontant(f.montantTTC)}</span>
                        </div>
                        <div className="paiement-info">
                          <span className="label">Date envoi</span>
                          <span className="value">{f.dateEnvoi ? new Date(f.dateEnvoi).toLocaleDateString('fr-FR') : '-'}</span>
                        </div>
                      </div>
                      {f.dateRelance && (
                        <div style={{ padding: '0.5rem 0.75rem', background: '#FEF3C7', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem', color: 'var(--warning-dark)' }}>
                          Dernière relance : {new Date(f.dateRelance).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                    </div>
                    <div className="paiement-card-footer">
                      <button className="btn btn-sm btn-secondary">Relancer</button>
                      <button className="btn btn-sm btn-success">Marquer comme payé</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <CheckCircle size={48} />
              <h3>Aucune facture en retard</h3>
              <p>Toutes les factures sont à jour.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Facturation;
